import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Filter, Pencil, Plus, Trash2 } from 'lucide-react';
import { Popover } from '@mui/material';

import { globalConfirm } from '../GlobalSnackbar';
import Button from './primitives/Button';
import IconButton from './primitives/IconButton';
import SearchInput from './primitives/SearchInput';
import EmptyState from './primitives/EmptyState';
import PageShell from './primitives/PageShell';

export type SortDirection = 'asc' | 'desc' | null;

export type FilterType = 'boolean' | 'select';

export interface ColumnFilter {
  type: FilterType;
  options?: { value: string | boolean | null; label: string }[];
}

export interface DataTableColumn {
  key: string;
  label: string;
  render?: (value: unknown, row: unknown) => React.ReactNode;
  sortable?: boolean;
  sortFn?: (a: unknown, b: unknown) => number;
  filterable?: boolean;
  filter?: ColumnFilter;
  width?: number | string;
}

export interface BaseDataRow {
  id?: string | number;
  [key: string]: unknown;
}

interface DataTablePageV2Props<T extends BaseDataRow = BaseDataRow> {
  title: string;
  columns: DataTableColumn[];
  data: T[];
  isLoading: boolean;
  onCreate: () => void;
  onEdit: (item: T) => void;
  onDelete: (item: T) => void;
  createButtonText?: string;
  emptyMessage?: string;
}

interface SortState {
  columnKey: string | null;
  direction: SortDirection;
}

const HeaderCellSort = ({ active, direction }: { active: boolean; direction: SortDirection }) => {
  if (!active)
    return <ArrowUpDown size={12} strokeWidth={1.75} color="var(--shell-text-tertiary)" />;
  if (direction === 'asc')
    return <ArrowUp size={12} strokeWidth={2} color="var(--shell-accent-primary)" />;
  return <ArrowDown size={12} strokeWidth={2} color="var(--shell-accent-primary)" />;
};

const FilterMenu = ({
  column,
  value,
  onChange,
}: {
  column: DataTableColumn;
  value: string | boolean | null | undefined;
  onChange: (v: string) => void;
}) => {
  const items: { value: string; label: string; selected: boolean }[] = [];
  items.push({ value: 'all', label: 'Все', selected: value === undefined || value === null });
  if (column.filter?.type === 'boolean') {
    items.push({ value: 'true', label: 'Да', selected: value === true });
    items.push({ value: 'false', label: 'Нет', selected: value === false });
  } else if (column.filter?.type === 'select') {
    for (const opt of column.filter.options ?? []) {
      items.push({
        value: String(opt.value),
        label: opt.label,
        selected: opt.value === value,
      });
    }
  }

  return (
    <div style={{ minWidth: 180, padding: 6 }}>
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          onClick={() => onChange(it.value)}
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            padding: '6px 10px',
            border: 'none',
            background: it.selected ? 'var(--shell-bg-selected)' : 'transparent',
            color: 'var(--shell-text-primary)',
            fontSize: 13,
            borderRadius: 'var(--shell-radius-sm)',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => {
            if (!it.selected)
              (e.currentTarget as HTMLElement).style.background = 'var(--shell-bg-hover)';
          }}
          onMouseLeave={(e) => {
            if (!it.selected) (e.currentTarget as HTMLElement).style.background = 'transparent';
          }}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
};

const DataTablePageV2 = <T extends BaseDataRow = BaseDataRow>({
  title,
  columns,
  data,
  isLoading,
  onCreate,
  onEdit,
  onDelete,
  createButtonText = 'Создать',
  emptyMessage = 'Данные не найдены',
}: DataTablePageV2Props<T>) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortState, setSortState] = useState<SortState>({ columnKey: null, direction: null });
  const [filters, setFilters] = useState<Record<string, string | boolean | null>>({});
  const [filterAnchor, setFilterAnchor] = useState<{
    key: string;
    el: HTMLElement;
  } | null>(null);

  const handleSort = (key: string) => {
    const col = columns.find((c) => c.key === key);
    if (!col?.sortable) return;
    setSortState((prev) => {
      if (prev.columnKey !== key) return { columnKey: key, direction: 'asc' };
      if (prev.direction === 'asc') return { columnKey: key, direction: 'desc' };
      if (prev.direction === 'desc') return { columnKey: null, direction: null };
      return { columnKey: key, direction: 'asc' };
    });
  };

  const handleFilterChange = (key: string, raw: string) => {
    const col = columns.find((c) => c.key === key);
    setFilters((prev) => {
      const next = { ...prev };
      if (raw === 'all' || raw === '') {
        delete next[key];
      } else if (col?.filter?.type === 'boolean') {
        next[key] = raw === 'true';
      } else if (col?.filter?.type === 'select') {
        const opt = col.filter.options?.find((o) => String(o.value) === raw);
        if (opt) next[key] = opt.value;
      } else {
        next[key] = raw;
      }
      return next;
    });
    setFilterAnchor(null);
  };

  const filteredAndSorted = useMemo(() => {
    let result = data;

    result = result.filter((row) =>
      Object.entries(filters).every(([key, val]) => {
        const col = columns.find((c) => c.key === key);
        if (!col?.filterable) return true;
        return row[key] === val;
      })
    );

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const search = (v: unknown): boolean => {
        if (v == null) return false;
        if (typeof v === 'string') return v.toLowerCase().includes(q);
        if (typeof v === 'number' || typeof v === 'boolean')
          return String(v).toLowerCase().includes(q);
        if (Array.isArray(v)) return v.some(search);
        if (typeof v === 'object') return Object.values(v as object).some(search);
        return false;
      };
      result = result.filter((row) => columns.some((c) => search(row[c.key])));
    }

    if (sortState.columnKey && sortState.direction) {
      const key = sortState.columnKey;
      const col = columns.find((c) => c.key === key);
      if (col?.sortable) {
        result = [...result].sort((a, b) => {
          const av = a[key];
          const bv = b[key];
          const cmp = col.sortFn
            ? col.sortFn(av, bv)
            : String(av ?? '') < String(bv ?? '')
              ? -1
              : String(av ?? '') > String(bv ?? '')
                ? 1
                : 0;
          return sortState.direction === 'asc' ? cmp : -cmp;
        });
      }
    }
    return result;
  }, [data, columns, filters, searchQuery, sortState]);

  const handleDelete = (item: T) => {
    const name =
      (item as any).title ||
      (item as any).name ||
      (item as any).email ||
      (item as any).code ||
      'элемент';
    globalConfirm({
      message: `Вы уверены, что хотите удалить "${name}"?`,
      title: 'Подтверждение удаления',
      confirmText: 'Удалить',
      cancelText: 'Отмена',
      onConfirm: () => onDelete?.(item),
      onCancel: () => undefined,
    });
  };

  return (
    <PageShell
      title={title}
      toolbar={
        data && data.length > 0 ? (
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Поиск по таблице…"
            width={320}
          />
        ) : null
      }
      actions={
        <Button variant="primary" leftIcon={<Plus size={14} strokeWidth={2} />} onClick={onCreate}>
          {createButtonText}
        </Button>
      }
      noContentPadding
    >
      {isLoading ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--shell-text-tertiary)',
            fontSize: 13,
          }}
        >
          Загрузка…
        </div>
      ) : !data || data.length === 0 ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <EmptyState title={emptyMessage} />
        </div>
      ) : (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            position: 'relative',
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: 0,
              fontSize: 13,
              color: 'var(--shell-text-primary)',
            }}
          >
            <thead>
              <tr>
                {columns.map((col) => {
                  const active = sortState.columnKey === col.key;
                  const hasFilter = filters[col.key] !== undefined && filters[col.key] !== null;
                  return (
                    <th
                      key={col.key}
                      style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        textAlign: 'left',
                        fontSize: 11,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        color: 'var(--shell-text-tertiary)',
                        background: 'var(--shell-bg-subtle)',
                        borderBottom: '1px solid var(--shell-border-subtle)',
                        padding: '0 12px',
                        height: 36,
                        whiteSpace: 'nowrap',
                        cursor: col.sortable ? 'pointer' : 'default',
                        userSelect: 'none',
                        width: col.width,
                      }}
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest('[data-filter-btn]')) return;
                        if (col.sortable) handleSort(col.key);
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {col.label}
                        {col.sortable && (
                          <HeaderCellSort active={active} direction={sortState.direction} />
                        )}
                        {col.filterable && col.filter && (
                          <button
                            type="button"
                            data-filter-btn
                            onClick={(e) => setFilterAnchor({ key: col.key, el: e.currentTarget })}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 20,
                              height: 20,
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              color: hasFilter
                                ? 'var(--shell-accent-primary)'
                                : 'var(--shell-text-tertiary)',
                              borderRadius: 4,
                            }}
                          >
                            <Filter size={12} strokeWidth={1.75} />
                          </button>
                        )}
                      </span>
                    </th>
                  );
                })}
                <th
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    width: 84,
                    background: 'var(--shell-bg-subtle)',
                    borderBottom: '1px solid var(--shell-border-subtle)',
                    padding: '0 12px',
                    height: 36,
                  }}
                />
              </tr>
            </thead>
            <tbody>
              {filteredAndSorted.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + 1}
                    style={{ padding: 0, background: 'var(--shell-bg-surface)' }}
                  >
                    <EmptyState
                      size="sm"
                      title={
                        searchQuery.trim() || Object.keys(filters).length > 0
                          ? 'По заданным фильтрам ничего не найдено'
                          : emptyMessage
                      }
                    />
                  </td>
                </tr>
              ) : (
                filteredAndSorted.map((row, idx) => (
                  <tr
                    key={(row.id as any) ?? idx}
                    style={{
                      transition: 'background var(--shell-transition-fast)',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.background = 'var(--shell-bg-subtle)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.background = 'transparent';
                    }}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        style={{
                          padding: '0 12px',
                          height: 40,
                          borderBottom: '1px solid var(--shell-border-subtle)',
                          color: 'var(--shell-text-primary)',
                          fontSize: 13,
                          verticalAlign: 'middle',
                        }}
                      >
                        {col.render ? col.render(row[col.key], row) : String(row[col.key] ?? '')}
                      </td>
                    ))}
                    <td
                      style={{
                        padding: '0 8px',
                        height: 40,
                        borderBottom: '1px solid var(--shell-border-subtle)',
                        verticalAlign: 'middle',
                        whiteSpace: 'nowrap',
                        textAlign: 'right',
                      }}
                    >
                      <IconButton
                        size="sm"
                        label="Редактировать"
                        onClick={() => onEdit(row)}
                        icon={<Pencil size={14} strokeWidth={1.75} />}
                      />
                      <IconButton
                        size="sm"
                        tone="danger"
                        label="Удалить"
                        onClick={() => handleDelete(row)}
                        icon={<Trash2 size={14} strokeWidth={1.75} />}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <Popover
            open={!!filterAnchor}
            anchorEl={filterAnchor?.el ?? null}
            onClose={() => setFilterAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
              paper: {
                sx: {
                  mt: 0.5,
                  border: '1px solid var(--shell-border-subtle)',
                  boxShadow: 'var(--shell-shadow-overlay)',
                  borderRadius: 'var(--shell-radius-lg)',
                  background: 'var(--shell-bg-surface)',
                },
              },
            }}
          >
            {filterAnchor &&
              (() => {
                const col = columns.find((c) => c.key === filterAnchor.key);
                if (!col) return null;
                return (
                  <FilterMenu
                    column={col}
                    value={filters[col.key]}
                    onChange={(v) => handleFilterChange(col.key, v)}
                  />
                );
              })()}
          </Popover>
        </div>
      )}
    </PageShell>
  );
};

export default DataTablePageV2;
